import React, { useEffect, useState, useCallback,useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Button,
    Box,
    Avatar,
    Divider,
    Link,
    CircularProgress,    
    LinearProgress
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Home as HomeIcon,
    Cancel as CancelIcon,
    Payment as PaymentIcon,
    Money as MoneyIcon,
    AccountBalance as AccountBalanceIcon,
    HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material';
import { VerifyAndUpdatePaymentStatus } from 'components/StripeConnect/logic/stripe-api';

const CustomerSuccessPage = () => {
    const navigate = useNavigate();
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    const [paymentStatus, setPaymentStatus] = useState('processing'); // 'processing', 'paid', 'failed'
    const [pollingCount, setPollingCount] = useState(0);
    const [error, setError] = useState(null);
    const pollIntervalRef = useRef(null);

    // Polling configuration
    const MAX_POLLING_ATTEMPTS = 15; // 5 minutes with 10-second intervals
    const POLLING_INTERVAL = 10000; // 10 seconds

    const verifyCheckout = useCallback(async (sessionId) => {
        if (!sessionId) {
            setError('Invalid session ID');
            setPaymentStatus('failed');
            return;
        }

        try {
            const response = await VerifyAndUpdatePaymentStatus(sessionId);
            
            if (response.isError) {
                throw new Error(response.error || 'Failed to verify payment');
            }

            const { paymentStatus, status } = response.result;
            const paymentStatusLower = paymentStatus?.toLowerCase();
            const statusLower = status?.toLowerCase();

            if (paymentStatusLower === 'paid') {
                setPaymentStatus('paid');
                clearInterval(pollIntervalRef.current);
            } else if (statusLower === 'complete' && paymentStatusLower === 'unpaid') {
                setPaymentStatus('processing');
            } else if (paymentStatusLower === 'unpaid' && statusLower !== 'complete') {
                setPaymentStatus('failed');
                clearInterval(pollIntervalRef.current);
            } else {
                setPaymentStatus('processing');
            }

            setError(null);
        } catch (error) {
            console.error("Error verifying checkout:", error);
            setError(error.message);
            // Don't set failed immediately on first error, keep polling
            if (pollingCount > 3) {
                setPaymentStatus('failed');
            }
        }
    }, [pollingCount]);

    useEffect(() => {
        if (!sessionId) {
            console.warn("Session ID is missing. Redirecting to home page.");
            navigate('/');
            return;
        }

        // Initial verification
        verifyCheckout(sessionId);

        // Set up polling
        pollIntervalRef.current = setInterval(() => {
            setPollingCount(prev => {
                const newCount = prev + 1;
                
                // Stop polling after max attempts
                if (newCount >= MAX_POLLING_ATTEMPTS) {
                    clearInterval(pollIntervalRef.current);
                    setPaymentStatus('failed');
                    setError('Payment verification timeout.');
                    return newCount;
                }

                // Continue polling
                verifyCheckout(sessionId);
                return newCount;
            });
        }, POLLING_INTERVAL);

        return () => clearInterval(pollIntervalRef.current);
    }, [sessionId, navigate, verifyCheckout]);

    const Processing = () => {
        const [elapsedTime, setElapsedTime] = useState(0);

        useEffect(() => {
            const timer = setInterval(() => {
                setElapsedTime(prev => prev + 1);
            }, 1000);

            return () => clearInterval(timer);
        }, []);

        const getTimeMessage = () => {
            if (elapsedTime < 30) return "We're verifying your payment details...";
            if (elapsedTime < 60) return "Still processing... This is normal for some payment methods.";
            return "Taking a bit longer than usual... We're still working on it.";
        };

        return (
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box sx={{ position: 'relative' }}>
                            <Avatar sx={{ bgcolor: 'warning.light', width: 80, height: 80 }}>
                                <AccountBalanceIcon sx={{ fontSize: 48 }} />
                            </Avatar>
                            <CircularProgress
                                size={88}
                                thickness={2}
                                sx={{
                                    color: 'warning.main',
                                    position: 'absolute',
                                    top: -4,
                                    left: -4,
                                }}
                            />
                        </Box>
                    </Box>

                    <Typography variant="h4" gutterBottom color="warning.dark">
                        Processing Payment
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                        {getTimeMessage()}
                    </Typography>

                    {/* Polling status */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Checking status...
                        </Typography>
                    </Box>

                    {/* Progress indicator */}
                    <Box sx={{ width: '100%', mb: 3 }}>
                        <LinearProgress 
                            color="warning" 
                            sx={{ height: 6, borderRadius: 3 }}
                            variant="indeterminate"
                        />
                    </Box>

                    {/* Status indicators */}
                    <Box sx={{ textAlign: 'left', mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mr: 1 }} />
                            Payment initiated
                        </Typography>
                        <Typography variant="body2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            {elapsedTime > 10 ? (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mr: 1 }} />
                            ) : (
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                            )}
                            Verifying transaction
                        </Typography>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            {paymentStatus === 'paid' ? (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16, mr: 1 }} />
                            ) : (
                                <HourglassEmptyIcon sx={{ color: 'text.disabled', fontSize: 16, mr: 1 }} />
                            )}
                            Completing payment
                        </Typography>
                    </Box>

                    {/* {error && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )} */}

                    <Box sx={{ mt: 3 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="warning"
                            onClick={() => navigate('/')}
                            startIcon={<HomeIcon />}
                            disabled
                        >
                            Please Wait...
                        </Button>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            🔒 Processing securely via Stripe
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Do not close this window
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        );
    };

    const Success = () => {
        return (
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        {/* <Avatar sx={{ bgcolor: 'success.light', width: 80, height: 80 }}>
                            <CheckCircleIcon sx={{ fontSize: 48 }} />
                        </Avatar> */}
                        <CheckCircleIcon 
                            color="#23A26D"
                            sx={{
                                fontSize: 80,
                                mb: 3,
                                color: '#23A26D'
                            }}
                        />
                    </Box>

                    <Typography variant="h4" gutterBottom>
                        Payment Successful!
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        Thank you for your purchase. Your payment has been processed successfully.
                        A receipt has been sent to your email.
                    </Typography>

                    {/* {pollingCount > 0 && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Payment verified after {pollingCount} attempt(s)
                        </Alert>
                    )} */}

                    <Box sx={{ mt: 4, '& > *': { mb: 2 } }}>
                        <Button
                            fullWidth
                            variant="contained"
                            color='main'
                            size="large"
                            onClick={() => navigate('/')}
                            startIcon={<HomeIcon />}
                            sx={{ mb: 2 }}
                        >
                            Return to Dashboard
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            color="main"
                            size="large"
                            onClick={() => navigate('/payments')}
                            startIcon={<MoneyIcon />}
                        >
                            View Payments
                        </Button>
                    </Box>

                    <Divider sx={{ my: 3 }} />                    
                </Paper>
            </Container>
        );
    };

    const Failed = () => {
        return (
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        {/* <Avatar sx={{ bgcolor: 'error.light', width: 80, height: 80 }}>
                            <CancelIcon sx={{ fontSize: 48 }} />
                        </Avatar> */}
                        <CancelIcon 
                            color="#23A26D"
                            sx={{
                                fontSize: 80,
                                mb: 3,
                                color: 'rgb(234, 84, 85)'
                            }}
                        />
                    </Box>
                    
                    <Typography variant="h4" gutterBottom>
                        Payment {pollingCount >= MAX_POLLING_ATTEMPTS ? 'Verification Timeout' : 'Failed'}
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        {pollingCount >= MAX_POLLING_ATTEMPTS 
                            ? 'We were unable to verify your payment status. Please check your email for confirmation or try again.'
                            : 'Your payment was not completed. You can try again or choose a different payment method.'
                        }
                    </Typography>

                    {/* {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )} */}

                    <Box sx={{ mt: 4, '& > *': { mb: 2 } }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            size="large"
                            onClick={() => navigate('/checkout')}
                            startIcon={<PaymentIcon />}
                            sx={{ mb: 2 }}
                        >
                            Try Again
                        </Button>

                        <Button
                            fullWidth
                            variant="contained"
                            color='main'
                            size="large"
                            onClick={() => navigate('/')}
                            startIcon={<HomeIcon />}
                        >
                            Return to Dashboard
                        </Button>
                    </Box>

                    <Divider sx={{ my: 3 }} />                    
                </Paper>
            </Container>
        );
    };

    // Render appropriate component based on payment status
    return <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
            textAlign: 'center',
            padding: 3,
            height: '100vh',
            background: 'linear-gradient(to bottom right, #daf0ff, #e3ebf2, #cbf5dd)',
        }}
    >
        {(() => {
            switch (paymentStatus) {
                case 'paid':
                    return <Success />;
                case 'failed':
                    return <Failed />;
                case 'processing':
                default:
                    return <Processing />;
            }
        })()}
    </Box>

};

export default CustomerSuccessPage;